import { Instance } from 'mobx-state-tree';
import * as React from 'react';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { TLCode } from '../../common/enums';
import { ColDef } from 'ag-grid-community';
import { inject, observer } from 'mobx-react';
import BaseGrid from '../common/grid/BaseGrid';
import styled from '@emotion/styled';
import { Button, Classes, Dialog, Intent, Radio, RadioGroup, Checkbox, Label, NumericInput } from '@blueprintjs/core';
import { Message } from '../../store/models/Message';
import { Form } from 'react-final-form';
import { PharmacyFormField, TextInputFormField, RTEFormField, GroupFormField } from '../common/form';
import TranslatedMessage from '../common/translation/TranslatedMessage';
import { Pharmacy } from '../../store/models/Pharmacy';
import { Group } from '../../store/models/Group';
import { IconNames } from '@blueprintjs/icons';
import { dateFilterNormal, renderGridDateTimeWithDetails } from '../../store/utils';
import { MessageStore } from '../../store/MessageStore';
import { PharmacyStore } from '../../store';
import { AuthenticationStore } from '../../store';
import RTEInput from '../common/input/basicInputs/RTEInput';
import Select from 'react-select';
import DateInput from '../common/input/DateInput';
import { DivLine } from '../common/Toolbox';

interface MessageTabProps extends WithTranslateProps {
  initialMessageId?: number;
  recipientId?: number;
  secretariatId?: number;
  pharmacyId?: number;
  pharmacyList: Array<Instance<typeof Pharmacy>>;
  groupList: Array<Instance<typeof Group>>;
  refresh: () => void;
  MessageStore?: Instance<typeof MessageStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
}

interface MessageTabState {
  newMessage?: any;
  preview?: any;
  currentMessage?: any;
  messageList: Array<Instance<typeof Message>>;
  isLoading: boolean;
  dutyDate?: Date;
  dutyType?: string;
  isDayOnlyChecked?: boolean;
}

@inject('MessageStore', 'AuthenticationStore', 'PharmacyStore')
@observer
class MessageTab extends React.Component<MessageTabProps, MessageTabState> {
  state = {
    isLoading: false,
    newMessage: undefined,
    preview: '',
    messageList: [],
  } as MessageTabState;

  _isMounted = false;

  createMarkup = (text) => {
    return { __html: text };
  };

  tooltipRenderer = (text) => {
    const content = '<span dangerouslySetInnerHTML={__html: ' + text.value + '}></span>';
    return content;
  };

  columns = [
    {
      headerName: this.props.translate(TLCode.MESSAGE_Date),
      field: 'createdOn',
      width: 150,
      sort: 'desc',
      cellRenderer: renderGridDateTimeWithDetails,
      filterParams: {
        textCustomComparator: dateFilterNormal,
      },
    },
    { headerName: this.props.translate(TLCode.MESSAGE_Sender), field: 'senderName', width: 125 },
    { headerName: this.props.translate(TLCode.MESSAGE_Recipient), field: 'type', width: 125 },
    { headerName: this.props.translate(TLCode.MESSAGE_Title), field: 'title', width: 300 },
  ] as ColDef[];

  componentDidMount = async () => {
    this._isMounted = true;
    await this.loadMessage();
    await this.props.PharmacyStore!.loadList();
  };

  componentWillUnmount() {
    this._isMounted = false;
  }

  buildGridData = () => {
    return this.state.messageList.map((msg) => ({
      messageId: msg.messageId,
      createdOn: msg.createdOn,
      senderName: msg.senderName,
      recipientName: msg.recipientName,
      title: msg.title,
      body: msg.body,
      type: msg.type,
    }));
  };

  newMessage = (message?: any) => {
    const { pharmacyId, secretariatId } = this.props;
    const isSecretariat = this.props.AuthenticationStore!.isSecretariat || this.props.AuthenticationStore!.isAdmin;
    const recipientId = isSecretariat
      ? message
        ? this.props.PharmacyStore!.dataList.find((ph) => ph.apbNumber == message.senderName)!.pharmacyId
        : null
      : secretariatId;
    const msg = {
      messageId: 0,
      body: '',
      title: null,
      senderId: isSecretariat ? secretariatId! : pharmacyId!,
      sender: isSecretariat ? 'secretariat' : 'pharmacy',
      recipientId: recipientId ? recipientId : isSecretariat ? null : secretariatId!,
      recipientIds: [],
      selectionMode: recipientId ? 'one' : '',
      dutyType: '',
      dutyDate: null,
      dayOnly: false,
      nightOnly: false,
      dayNight: false,
      radius: 0,
    };
    this.setState({ newMessage: msg });
  };

  testMessage = (message?: any) => {
    const { secretariatId } = this.props;
    const isSecretariat = this.props.AuthenticationStore!.isSecretariat || this.props.AuthenticationStore!.isAdmin;
    const msg = {
      messageId: 0,
      body: '',
      title: 'Lorem ipsum dolor',
      senderId: secretariatId!,
      sender: 'secretariat',
      recipientId: null,
      recipientIds: [],
      selectionMode: 'test',
    };
    this.setState({ newMessage: msg });
  };

  stopEdition = () => {
    this._isMounted && this.setState({ newMessage: undefined });
    console.log('SET STATE UNDEFINED');
  };

  setRecipients = (data) => {
    const msg = this.state.newMessage;
    msg.selectionMode = data;
    this.setState({ newMessage: msg });
  };

  private onRowClicked = (ev) => {
    this.setState({ preview: ev.data.body, currentMessage: ev.data });
  };

  loadMessage = async () => {
    this._isMounted && this.setState({ isLoading: true });
    const criteriaMessage: any = { secretariatId: this.props.secretariatId, pharmacyId: this.props.pharmacyId };
    const res: any = await this.props.MessageStore!.search(criteriaMessage);
    this._isMounted && this.setState({ messageList: res });
    this._isMounted && this.setState({ isLoading: false });
  };

  save = async (newMessage: any) => {
    newMessage.selectionMode = this.state.newMessage.selectionMode;
    newMessage.dutyDate = this.state.newMessage.dutyDate;
    newMessage.dutyType = this.state.newMessage.dutyType;
    newMessage.dayOnly = this.state.newMessage.dayOnly;
    newMessage.nightOnly = this.state.newMessage.nightOnly;
    newMessage.dayNight = this.state.newMessage.dayNight;
    newMessage.radius = this.state.newMessage.radius;
    newMessage.recipientIds = this.state.newMessage.recipientIds.map((element) => element.value);
    this.stopEdition();
    await this.saveMessage(newMessage);
  };

  saveMessage = async (data: any) => {
    await this.props.MessageStore!.sendMessage(data);
    await this.loadMessage();
  };

  selectedDutyDateChange = async (date) => {
    const msg = this.state.newMessage;
    msg.dutyDate = date;
    this._isMounted && this.setState({ newMessage: msg });
  };

  selectedDutyTypeChange = async (type) => {
    const msg = this.state.newMessage;
    msg.dutyType = type;
    this._isMounted && this.setState({ newMessage: msg });
  };

  handleDayOnlyChange = (data) => {
    let newMessage = this.state.newMessage;
    newMessage.dayOnly = data.currentTarget.checked;
    this.setState({ newMessage: newMessage });
  };

  handleNightOnlyChange = (data) => {
    let newMessage = this.state.newMessage;
    newMessage.nightOnly = data.currentTarget.checked;
    this.setState({ newMessage: newMessage });
  };

  handleDayNightChange = (data) => {
    let newMessage = this.state.newMessage;
    newMessage.dayNight = data.currentTarget.checked;
    this.setState({ newMessage: newMessage });
  };

  handleMultiSelectChange = (value) => {
    let newMessage = this.state.newMessage;
    newMessage.recipientIds = value;
    this.setState({ newMessage: newMessage });
  };

  handleRadiusChange = (valueAsNumber, valueAsString) => {
    let newMessage = this.state.newMessage;
    newMessage.radius = valueAsNumber;
    this.setState({ newMessage: newMessage });
  };

  render() {
    const { newMessage } = this.state;
    const { translate, pharmacyId, pharmacyList, groupList } = this.props;

    return (
      <Container>
        <ButtonsContainer>
          <Button
            text={translate(TLCode.GENERAL_Refresh)}
            intent={Intent.PRIMARY}
            onClick={() => (this.props.refresh ? this.props.refresh() : this.loadMessage())}
          />
          <Button
            text={translate(TLCode.MESSAGE_NewMessage)}
            intent={Intent.PRIMARY}
            onClick={() => this.newMessage()}
          />
          {this.props.AuthenticationStore!.isSuperAdmin && (
            <Button
              text={translate(TLCode.MESSAGE_TestMessage)}
              intent={Intent.PRIMARY}
              onClick={() => this.testMessage()}
            />
          )}
        </ButtonsContainer>
        <Cols>
          <Col>
            <BaseGrid
              columnDefs={this.columns}
              rowData={this.buildGridData()}
              keyName={'messageId'}
              onRowClicked={this.onRowClicked}
              loading={this.state.isLoading}
            />
          </Col>
          <Col>
            <MessageContainer>
              <RTEInput readOnly={true} value={this.state.preview} />
            </MessageContainer>
          </Col>
        </Cols>
        {newMessage && (
          <Dialog
            isOpen={newMessage !== undefined}
            onClose={this.stopEdition}
            icon={IconNames.EDIT}
            title={<TranslatedMessage id={TLCode.MESSAGE_NewMessage} />}
            style={{ width: '900px' }}
          >
            <div className={Classes.DIALOG_BODY}>
              <Form
                initialValues={newMessage}
                onSubmit={this.save}
                render={({ handleSubmit, hasValidationErrors, dirty }) => {
                  return (
                    <form onSubmit={handleSubmit}>
                      {pharmacyId == null && newMessage.selectionMode != 'test' && (
                        <>
                          <RadioGroup
                            selectedValue={newMessage.selectionMode}
                            onChange={(data) => this.setRecipients(data.currentTarget.value)}
                          >
                            <Radio
                              key={'affialiates-1'}
                              label={translate(TLCode.MESSAGE_RECIPIENTS_Affiliates_1)}
                              value="affiliates-1"
                              disabled={newMessage.recipientId}
                            />
                            <Radio
                              key={'affiliates-3'}
                              label={translate(TLCode.MESSAGE_RECIPIENTS_Affiliates_3)}
                              value="affiliates-3"
                              disabled={newMessage.recipientId}
                            />
                            <Radio
                              key={'affiliates'}
                              label={translate(TLCode.MESSAGE_RECIPIENTS_Affiliates_All)}
                              value="affiliates"
                              disabled={newMessage.recipientId}
                            />
                            <Radio
                              key={'group'}
                              label={translate(TLCode.MESSAGE_RECIPIENTS_Group)}
                              value="affiliates-group"
                            />
                            <GroupFormField
                              id="groupSelect"
                              label={TLCode.PHARMACIES_Group}
                              name="group"
                              groupList={groupList}
                              isClearable={true}
                              disabled={newMessage.selectionMode !== 'affiliates-group'}
                            />
                            <Radio key={'one'} label={translate(TLCode.MESSAGE_RECIPIENTS_One)} value="one" />
                            <PharmacyFormField
                              id="pharmacySelect"
                              name="recipientId"
                              pharmacyList={pharmacyList}
                              required={newMessage.selectionMode == 'one'}
                              disabled={newMessage.selectionMode !== 'one'}
                            />
                            <Radio
                              key={'several'}
                              label={translate(TLCode.MESSAGE_RECIPIENTS_Several)}
                              value="several"
                            />
                            <Select
                              options={pharmacyList.map((elem) => {
                                return {
                                  value: elem.pharmacyId,
                                  label:
                                    '(' +
                                    elem.level +
                                    '- group ' +
                                    elem.group +
                                    ') - ' +
                                    elem.apbNumber +
                                    ' ' +
                                    elem.name +
                                    ' @ ' +
                                    elem.city,
                                };
                              })}
                              required={newMessage.selectionMode == 'several'}
                              disabled={newMessage.selectionMode !== 'several'}
                              isMulti
                              value={newMessage.recipientIds}
                              onChange={(value) => this.handleMultiSelectChange(value)}
                            />
                            <br />
                            <Radio
                              key={'onDuty'}
                              label={translate(TLCode.MESSAGE_RECIPIENTS_PharmaciesOnDuty)}
                              value="onDuty"
                            />
                            <DivLine>
                              <DivColumn>
                                {newMessage.selectionMode == 'onDuty' && (
                                  <Label>
                                    <b>
                                      <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_DutyDate} />
                                    </b>
                                    &nbsp;&nbsp;
                                  </Label>
                                )}
                                {newMessage.selectionMode !== 'onDuty' && (
                                  <Label style={{ color: '#aaa' }}>
                                    <b>
                                      <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_DutyDate} />
                                    </b>
                                    &nbsp;&nbsp;
                                  </Label>
                                )}
                                <DateInput
                                  value={newMessage.dutyDate}
                                  canClearSelection={false}
                                  onChange={(value) => {
                                    const utcDate = new Date(
                                      Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()),
                                    );
                                    this.selectedDutyDateChange(utcDate);
                                  }}
                                  disabled={newMessage.selectionMode !== 'onDuty'}
                                />
                              </DivColumn>
                              <DivColumn>
                                <Checkbox
                                  name="dayOnly"
                                  value={'dayOnly'}
                                  checked={this.state.newMessage.dayOnly}
                                  onChange={this.handleDayOnlyChange}
                                  disabled={newMessage.selectionMode !== 'onDuty'}
                                >
                                  <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_DayOnlyDuty} />
                                </Checkbox>
                                <Checkbox
                                  name="nightOnly"
                                  value={'nightOnly'}
                                  checked={this.state.newMessage.nightOnly}
                                  onChange={this.handleNightOnlyChange}
                                  disabled={newMessage.selectionMode !== 'onDuty'}
                                >
                                  <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_NightOnlyDuty} />
                                </Checkbox>
                                <Checkbox
                                  name="dayNight"
                                  value={'dayNight'}
                                  checked={this.state.newMessage.dayNight}
                                  onChange={this.handleDayNightChange}
                                  disabled={newMessage.selectionMode !== 'onDuty'}
                                >
                                  <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_DayNightDuty} />
                                </Checkbox>
                              </DivColumn>
                            </DivLine>
                            <Radio
                              key={'neighbors'}
                              label={translate(TLCode.MESSAGE_RECIPIENTS_Neighbors)}
                              value="neighbors"
                            />
                            <DivLine>
                              <DivColumn>
                                <PharmacyFormField
                                  id="pharmacySelectNeighbors"
                                  name="recipientId"
                                  pharmacyList={pharmacyList}
                                  required={newMessage.selectionMode == 'neighbors'}
                                  disabled={newMessage.selectionMode !== 'neighbors'}
                                />
                              </DivColumn>
                              <DivColumn>
                                <NumericInput
                                  id="radius"
                                  name="radius"
                                  required={newMessage.selectionMode == 'neighbors'}
                                  disabled={newMessage.selectionMode !== 'neighbors'}
                                  onValueChange={this.handleRadiusChange}
                                  value={this.state.newMessage.radius}
                                  majorStepSize={1000}
                                  stepSize={100}
                                />
                                {this.props.translate(TLCode.MESSAGE_RECIPIENTS_Neighbors_radius)}
                              </DivColumn>
                            </DivLine>
                          </RadioGroup>
                        </>
                      )}
                      {newMessage.selectionMode == 'test' && (
                        <>
                          <TextInputFormField
                            name="testEmailAddress"
                            label={TLCode.MESSAGE_TestEmailAddress}
                            inline={true}
                            required={true}
                            fill={true}
                          />
                        </>
                      )}
                      <TextInputFormField
                        name="title"
                        label={TLCode.MESSAGE_Title}
                        inline={true}
                        required={true}
                        fill={true}
                      />
                      <div>
                        <RTEFormField name="body" label={TLCode.MESSAGE_Content} />
                      </div>
                      <div className={Classes.DIALOG_FOOTER}>
                        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                          <Button text={<TranslatedMessage id={TLCode.GENERAL_Cancel} />} onClick={this.stopEdition} />
                          <Button
                            type="submit"
                            intent={Intent.PRIMARY}
                            disabled={hasValidationErrors || !dirty}
                            text={<TranslatedMessage id={TLCode.GENERAL_Send} />}
                          />
                        </div>
                      </div>
                    </form>
                  );
                }}
              />
            </div>
          </Dialog>
        )}
      </Container>
    );
  }
}

export default withTranslate(MessageTab);

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Cols = styled.div`
  display: flex;
  width: 100%
  height: 100%;
  flex-direction: row;
  justify-content: space-between;
  flex: 1;
`;

const Col = styled.div`
  display: flex;
  width: 50%
  height: 100%;
  flex-direction: row;
  justify-content: space-between;
  flex: 1;
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  flex: 1;
  border: 1px solid black;
  margin: 20px 0 0 10px;
`;

const ButtonsContainer = styled.div({
  display: 'flex',
  alignContent: 'center',
  justifyContent: 'flex-end',
  marginBottom: 5,
  '& button': {
    marginLeft: 5,
  },
});

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 900px;
  margin-right: 20px;
`;
